import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PolarAngleAxis,
} from "recharts";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";

const COLORS = ["#38BDF8", "#10B981", "#FBBF24", "#F43F5E", "#A855F7", "#EC4899"];

function money(v) {
  return new Intl.NumberFormat("en-US", { notation: "compact", compactDisplay: "short", style: "currency", currency: "USD", maximumFractionDigits: 1 }).format(v || 0);
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/analytics/dashboard"), api.get("/contracts")]).then(([a, c]) => {
      setData(a.data);
      setContracts(c.data);
    });
  }, []);

  if (!data) return <PageShell title="Analytics" subtitle="Loading"><div className="h-64 glass animate-pulse" /></PageShell>;

  const kpis = data.kpis;

  const byCounterparty = contracts.reduce((acc, c) => {
    acc[c.counterparty] = (acc[c.counterparty] || 0) + (c.value_amount || 0);
    return acc;
  }, {});
  const cpChart = Object.entries(byCounterparty).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);

  return (
    <PageShell title="Analytics & Reporting" subtitle="Portfolio-wide intelligence">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="p-6">
          <div className="overline">Total value</div>
          <div className="text-3xl font-semibold mt-2 tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>{money(kpis.total_value)}</div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="overline">Active contracts</div>
          <div className="text-3xl font-semibold mt-2 tracking-tighter font-mono" style={{ fontFamily: 'var(--font-heading)' }}>{kpis.active_contracts}</div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="overline">Counterparties</div>
          <div className="text-3xl font-semibold mt-2 tracking-tighter font-mono" style={{ fontFamily: 'var(--font-heading)' }}>{kpis.counterparties}</div>
        </GlassCard>
        <GlassCard className="p-6">
          <div className="overline">High-risk</div>
          <div className="text-3xl font-semibold mt-2 tracking-tighter font-mono" style={{ fontFamily: 'var(--font-heading)', color: kpis.high_risk_contracts > 0 ? "#F43F5E" : "var(--text)" }}>{kpis.high_risk_contracts}</div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-8">
          <GlassCard className="p-6 h-full">
            <div className="overline mb-4">Contract velocity & portfolio value</div>
            <div className="h-72">
              <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthly_volume}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#38BDF8" stopOpacity={0.6} /><stop offset="100%" stopColor="#38BDF8" stopOpacity={0} /></linearGradient>
                    <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" stroke="var(--text-3)" fontSize={11} />
                  <YAxis yAxisId="left" stroke="var(--text-3)" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke="var(--text-3)" fontSize={11} tickFormatter={money} />
                  <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="contracts" stroke="#38BDF8" fill="url(#g1)" strokeWidth={2} name="Contracts" />
                  <Area yAxisId="right" type="monotone" dataKey="value" stroke="#10B981" fill="url(#g2)" strokeWidth={2} name="Value (USD)" />
                </AreaChart>
              </ResponsiveContainer></div>
            </div>
          </GlassCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-4">
          <GlassCard className="p-6 h-full">
            <div className="overline mb-4">Risk distribution</div>
            <div className="h-56">
              <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.risk_distribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {data.risk_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer></div>
            </div>
            <div className="space-y-1.5 mt-3">
              {data.risk_distribution.map((r, i) => (
                <div key={r.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /><span className="capitalize">{r.name}</span></div>
                  <span className="font-mono text-[var(--text-2)]">{r.value}</span>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-6">
          <GlassCard className="p-6 h-full">
            <div className="overline mb-4">Top counterparties by value</div>
            <div className="h-64">
              <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
                <BarChart data={cpChart} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis type="number" stroke="var(--text-3)" fontSize={11} tickFormatter={money} />
                  <YAxis type="category" dataKey="name" stroke="var(--text-3)" fontSize={11} width={140} />
                  <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} formatter={(v) => money(v)} />
                  <Bar dataKey="value" fill="#38BDF8" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer></div>
            </div>
          </GlassCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="lg:col-span-6">
          <GlassCard className="p-6 h-full">
            <div className="overline mb-4">Pipeline status</div>
            <div className="h-64">
              <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.status_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" stroke="var(--text-3)" fontSize={11} />
                  <YAxis stroke="var(--text-3)" fontSize={11} />
                  <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} />
                  <Bar dataKey="value" fill="#10B981" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer></div>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </PageShell>
  );
}
